import type { DataTableMode } from './types';
import { cx } from './utils';

/**
 * Segmented paged / scroll switch, matching the pair the apps already ship in
 * `dashboard/.../PatientTable.tsx` and `files-viewer/.../work-queue/CaseList.tsx`
 * so the control reads the same everywhere.
 *
 * The semantics here are the work queue's, not the dashboard's: the dashboard's
 * "scroll" view slices an already-fetched array client-side, whereas this one -
 * like the work queue's - keeps asking the server for the next chunk. Same
 * affordance, and the one that actually scales.
 *
 * Lives in the footer, next to whatever that mode renders there (a pager or a
 * loaded-vs-total count) - the footer is the one place present in every mode.
 */
export interface DataTableModeToggleProps {
    mode: DataTableMode;
    onChange: (mode: DataTableMode) => void;
    className?: string;
}

const BUTTON_BASE =
    'p-1.5 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400';
const BUTTON_ACTIVE = 'bg-white shadow-sm text-gray-700';
const BUTTON_IDLE = 'text-gray-400 hover:text-gray-600';

export function DataTableModeToggle({ mode, onChange, className }: DataTableModeToggleProps) {
    return (
        <div className={cx('flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5', className)}>
            <button
                type="button"
                onClick={() => onChange('paged')}
                aria-label="Paginated view"
                aria-pressed={mode === 'paged'}
                title="Paginated view"
                className={cx(BUTTON_BASE, mode === 'paged' ? BUTTON_ACTIVE : BUTTON_IDLE)}
            >
                <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="h-4 w-4"
                    aria-hidden="true"
                >
                    <rect x="2" y="2" width="12" height="5" rx="1" />
                    <rect x="2" y="9" width="12" height="5" rx="1" />
                </svg>
            </button>
            <button
                type="button"
                onClick={() => onChange('infinite')}
                aria-label="Scrollable view"
                aria-pressed={mode === 'infinite'}
                title="Scrollable view"
                className={cx(BUTTON_BASE, mode === 'infinite' ? BUTTON_ACTIVE : BUTTON_IDLE)}
            >
                <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="h-4 w-4"
                    aria-hidden="true"
                >
                    <rect x="2" y="1" width="12" height="14" rx="1" />
                    <path d="M5 5h6M5 8h6M5 11h6" />
                </svg>
            </button>
        </div>
    );
}
